import { useState } from "react";
import { api } from "@/lib/api";
import { Mail, MapPin, CheckCircle2 } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTranslation } from "react-i18next";

const OBJETS = ["Démo", "Question plateforme", "Projet conseil", "Partenariat", "Autre"];

export default function Contact() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  useSEO({
    title: "Contact · Noam by Constante Partners",
    description: "Échangez avec l'équipe Constante Partners pour une démo ou un projet de conseil. Réponse sous 48 heures.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    prenom: "", nom: "", organisation: "", email: "", objet: "Démo", message: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: null });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: null });
    try {
      await api.post("/contact", form);
      setStatus({ loading: false, ok: true, error: null });
      setForm({ prenom: "", nom: "", organisation: "", email: "", objet: "Démo", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: err?.response?.data?.detail || "Erreur d'envoi." });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="noam-overline mb-3">Contact</div>
        <h1 className="noam-display text-4xl sm:text-5xl font-bold tracking-tight">
          {en ? "Let's talk about your" : "Parlons de votre"}<br /><span className="text-[#378ADD]">{en ? "next decision." : "prochaine décision."}</span>
        </h1>
        <p className="text-zinc-300 mt-5">
          {en ? "Demo, platform question, advisory project or partnership: we reply within 24 business hours." : "Démo, question plateforme, projet conseil ou partenariat : nous répondons sous 24 h ouvrées."}
        </p>

        <div className="mt-10 space-y-4 text-sm">
          <div className="flex items-start gap-3" data-testid="contact-address">
            <MapPin className="w-4 h-4 mt-0.5 text-[#378ADD]" />
            <div>
              <div className="text-white">Constante Partners</div>
              <div className="text-zinc-400">30 boulevard Helvétique · 1207 Genève · Suisse</div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="contact-email">
            <Mail className="w-4 h-4 text-[#378ADD]" />
            <span className="text-zinc-300">contact@noam.ch</span>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 noam-card p-8" data-testid="contact-form">
        {status.ok && (
          <div className="mb-6 p-4 rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/10 flex items-center gap-3 text-sm" data-testid="contact-success">
            <CheckCircle2 className="w-4 h-4 text-[#1D9E75]" />
            {en ? "Message sent. We will get back to you shortly." : "Message envoyé. Nous vous recontactons rapidement."}
          </div>
        )}
        {status.error && (
          <div className="mb-6 p-4 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-sm text-[#E24B4A]" data-testid="contact-error">
            {status.error}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{en ? "First name" : "Prénom"}</label>
            <input required value={form.prenom} onChange={update("prenom")} className="noam-input" data-testid="contact-prenom" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{en ? "Last name" : "Nom"}</label>
            <input required value={form.nom} onChange={update("nom")} className="noam-input" data-testid="contact-nom" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-zinc-400 mb-1 block">{en ? "Organization" : "Organisation"}</label>
            <input value={form.organisation} onChange={update("organisation")} className="noam-input" data-testid="contact-organisation" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-zinc-400 mb-1 block">{en ? "Business email" : "Email professionnel"}</label>
            <input type="email" required value={form.email} onChange={update("email")} className="noam-input" data-testid="contact-email-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-zinc-400 mb-1 block">{en ? "Subject" : "Objet"}</label>
            <select value={form.objet} onChange={update("objet")} className="noam-input" data-testid="contact-objet">
              {OBJETS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-zinc-400 mb-1 block">Message</label>
            <textarea required rows={5} value={form.message} onChange={update("message")} className="noam-input resize-none" data-testid="contact-message" />
          </div>
        </div>
        <button type="submit" disabled={status.loading} className="btn-primary mt-6 disabled:opacity-50" data-testid="contact-submit">
          {status.loading ? (en ? "Sending…" : "Envoi…") : (en ? "Send" : "Envoyer")}
        </button>
      </form>
    </div>
  );
}
